import { Action, Reducer } from "redux";
import { ApplicationState, AppThunkAction, ModelIsValidAction, MODEL_IS_VALID, remoteNOActions } from ".";
import { RECEIVE_INITIAL } from "../actions";
import { ADDON_CHANGED, IAddOn, IAddOnActionData, MILLAGE_UNDER_150000_CHANGED, NO_ADDONS_COVERAGE_CHANGED } from "../AddOn";
import {
  CoverageChangedType,
  footerDefaults,
  FooterState,
  FOOTER_CHANGED,
  FOOTER_COVERAGE_CHANGED,
  GetUpdatedNoFooterPrices,
  FooterAction,
  createFooterIsVisibleAction,
  createFooterContentAction,
} from "../Footer";
import { Coverage, INSURANCE_SELECTED_COVERAGE_CHANGED, INSURANCE_SELF_RISK_CHANGED } from "../Insurance";
import {
  ACTUAL_MILAGE_CHANGED,
  CONTACTS_EMAIL_CHANGED,
  CONTACTS_PHONE_CHANGED,
  defaults as profileDefaults,
  GDPR_CHANGED,
  GDPR_CHECK,
  ProfileState,
} from "../Profile";
import { YoungDriverAction, YOUNG_DRIVER_CHANGED } from "../YoungDriver";
import { BONUS_CHANGED, ValidAction } from "./Bonus";
import { IMPORTED_USED_CHANGED, ImportedUsedAction } from "../ImportedUsed";
import { DISTANCE_NO_CHANGED, DistanceNoState } from "./DistanceNo";

export interface ReceiveAction {
  type: typeof RECEIVE_INITIAL;
  payload: any;
}

type DistanceNOChangedAction = {
  type: typeof DISTANCE_NO_CHANGED;
  payload: { id: string };
};

type AddOnAction = {
  type: typeof ADDON_CHANGED | typeof NO_ADDONS_COVERAGE_CHANGED | typeof MILLAGE_UNDER_150000_CHANGED;
  data: any;
};

export type ProfileAction = {
  type:
  | typeof CONTACTS_EMAIL_CHANGED
  | typeof CONTACTS_PHONE_CHANGED
  | typeof ACTUAL_MILAGE_CHANGED
  | typeof GDPR_CHANGED
  | typeof GDPR_CHECK;
  payload: { value: any };
};

const distanceDefaults = {
  selectedId: "D12000",
  help: {
    title: "Hur långt kör du per år?",
    description: "Välj det milintervall som motsvarar hur många mil du kör på ett år. Priset på försäkringen påverkas av hur långt du kör.",
  },
  label: {
    id: "2",
    title: "Hur många mil kör du med bilen per år?",
  },
  values: [
    {
      id: "D5000",
      value: 5000,
      unit: "kilometers",
      title: "5000 km",
    },
    {
      id: "D8000",
      value: 8000,
      unit: "kilometers",
      title: "8000 km",
    },
    {
      id: "D12000",
      value: 12000,
      unit: "kilometers",
      title: "12000 km",
    },
    {
      id: "D16000",
      value: 16000,
      unit: "kilometers",
      title: "16000 km",
    },
    {
      id: "D20000",
      value: 20000,
      unit: "kilometers",
      title: "20000 km",
    },
    {
      id: "D25000",
      value: 25000,
      unit: "kilometers",
      title: "25000 km",
    },
    {
      id: "D30000",
      value: 30000,
      unit: "kilometers",
      title: "30000 km",
    },
    {
      id: "D35000",
      value: 35000,
      unit: "kilometers",
      title: "35000 km",
    },
    {
      id: "D4000",
      value: 40000,
      unit: "kilometers",
      title: "40000 km",
    },
  ],
};

type ProfileValidAction = ProfileAction & ModelIsValidAction;

//const emailRegEx: RegExp = /^[a-zA-Z0-9_.-]+@[a-zA-Z0-9-._]+\.[a-zA-Z]{2,4}$/;
const emailRegEx: RegExp = /^[a-zA-Z0-9_-]+(?:\.[a-zA-Z0-9_-]+)*@[a-zA-Z0-9_-]{2,}(?:\.[a-z]{2,})+$/;
const phoneRegEx: RegExp = /^((\+|00)47)\d{8}$/;
const milageRegEx: RegExp = /^[0-9]{1,6}$/;

const getInputState = (value: string, pattern: RegExp) => (value.length > 0 ? (pattern.test(value) ? 0 : 2) : 1);

const isValid = (state: ProfileState): ProfileState => {
  const emailState = getInputState(state.email.value ?? "", emailRegEx);
  const phoneState = getInputState(state.phone.value ?? "", phoneRegEx);
  const millageState = getInputState(state.actualMilage?.value ?? "", milageRegEx);
  const isValid = emailState === 0 && phoneState === 0 && millageState === 0;

  if (
    state.email.state === emailState &&
    state.phone.state === phoneState &&
    state.actualMilage?.state === millageState &&
    state.isValid === isValid
  ) {
    return state;
  }

  return {
    ...state,
    email: {
      ...state.email,
      state: emailState,
    },
    phone: {
      ...state.phone,
      state: phoneState,
    },
    actualMilage: {
      ...state.actualMilage,
      state: millageState,
      stateDescriptions: state.actualMilage?.stateDescriptions ?? [],
    },
    isValid: isValid,
  };
};

const isGDPRchecked = (state: ProfileState): ProfileState => {
  if (state.gdpr?.selected) {
    return state;
  }
  return {
    ...state,
    gdpr: {
      ...state.gdpr,
      showError: true,
      selected: false,
    },
  };
};

export const distanceNoReducer: Reducer<DistanceNoState> = (state: DistanceNoState = distanceDefaults, input: Action): DistanceNoState => {
  const action = input as DistanceNOChangedAction;

  switch (action?.type) {
    case DISTANCE_NO_CHANGED:
      return { ...state, selectedId: action.payload.id };
    default:
      return state;
  }
};

export const footerReducer: Reducer<FooterState> = (state: FooterState = footerDefaults, input: Action): FooterState => {
  const action = input as FooterAction;

  switch (action?.type) {
    case FOOTER_CHANGED:
      return { ...state, expanded: action.payload.value as boolean };
    case FOOTER_COVERAGE_CHANGED:
      const data = action.payload.value as CoverageChangedType;
      return GetUpdatedNoFooterPrices(state, data.coverage, data.selectedAddonInternalCodes);
    default:
      return state;
  }
};

export const profileReducer: Reducer<ProfileState> = (
  state: ProfileState = profileDefaults,
  input: Action | ModelIsValidAction,
): ProfileState => {
  const action: ProfileAction | ModelIsValidAction = input as ProfileValidAction;

  switch (action?.type) {
    case CONTACTS_EMAIL_CHANGED: {
      const value = action.payload.value ?? "";
      if (state.email.value === value) {
        return state;
      }

      return {
        ...state,
        email: {
          ...state.email,
          value,
          secureValue: undefined,
          state: getInputState(value, emailRegEx),
        },
      };
    }
    case CONTACTS_PHONE_CHANGED: {
      const value = action.payload.value ?? "";
      if (state.phone.value === value) {
        return state;
      }
      return {
        ...state,
        phone: {
          ...state.phone,
          value,
          secureValue: undefined,
          state: getInputState(value, phoneRegEx),
        },
      };
    }
    case ACTUAL_MILAGE_CHANGED: {
      const value = action.payload.value ?? "";
      if (state?.actualMilage?.value === value) {
        return state;
      }

      return {
        ...state,
        actualMilage: {
          ...state.actualMilage,
          value,
          secureValue: undefined,
          state: getInputState(value, milageRegEx),
          stateDescriptions: state.actualMilage?.stateDescriptions ?? [],
        },
      };
    }
    case MODEL_IS_VALID: {
      return isValid(state);
    }
    case GDPR_CHANGED:
      const newValue = action.payload.value as boolean;

      return { ...state, gdpr: { ...state.gdpr, selected: newValue, showError: false } };

    case GDPR_CHECK: {
      return isGDPRchecked(state);
    }
    default:
      return state;
  }
};

export function createReceiveInitialData(payload: any): ReceiveAction {
  return {
    type: RECEIVE_INITIAL,
    payload: payload,
  };
}

export const actions = {
  youngDriverChanged:
    (value: unknown): AppThunkAction<YoungDriverAction | AppThunkAction<{}>> =>
      dispatch => {
        dispatch({
          type: YOUNG_DRIVER_CHANGED,
          payload: { value: value as boolean },
        });
        dispatch(remoteNOActions.requestPriceData());
      },
  importedUsedChanged:
    (value: unknown): AppThunkAction<ImportedUsedAction | AppThunkAction<{}>> =>
      dispatch => {
        dispatch({
          type: IMPORTED_USED_CHANGED,
          payload: { value: value as boolean },
        });
        dispatch(remoteNOActions.requestQuote())
      },
  bonusChanged:
    (value: unknown): AppThunkAction<ValidAction | {}> =>
      dispatch => {
        dispatch({
          type: BONUS_CHANGED,
          payload: { value: value as string },
        });
        dispatch(remoteNOActions.requestPriceData());
      },
  distanceNoChanged:
    (value: unknown): AppThunkAction<DistanceNOChangedAction | AppThunkAction<{}>> =>
      dispatch => {
        dispatch({
          type: DISTANCE_NO_CHANGED,
          payload: { id: value as string },
        });
        dispatch(remoteNOActions.requestPriceData());
      },
  insuranceSelfRiskChanged:
    (value: string): AppThunkAction<ValidAction | {}> =>
      dispatch => {
        dispatch({
          type: INSURANCE_SELF_RISK_CHANGED,
          payload: { value: value },
        });

        dispatch(remoteNOActions.requestPriceData());
      },

  addonChanged:
    (value: IAddOnActionData): AppThunkAction<AddOnAction | AppThunkAction<{}>> =>
      dispatch => {
        dispatch({
          type: ADDON_CHANGED,
          data: value as IAddOnActionData,
        });

        dispatch(remoteNOActions.requestPriceData());
      },
  lowMillageValueChanged:
    (value: unknown): AppThunkAction<AddOnAction> =>
      dispatch => {
        dispatch({
          type: MILLAGE_UNDER_150000_CHANGED,
          data: value as IAddOnActionData,
        });
      },

  footerExpanded:
    (value: boolean): AppThunkAction<FooterAction> =>
      dispatch =>
        dispatch(createFooterIsVisibleAction(value)),

  coverageChanged:
    (coverage: Coverage, selectedAddonInternalCodes: string[]): AppThunkAction<FooterAction> =>
      dispatch =>
        dispatch(createFooterContentAction({ coverage, selectedAddonInternalCodes })),

  coverageNoChanged:
    (value?: IAddOn[]): AppThunkAction<AddOnAction | AppThunkAction<{}>> =>
      dispatch => {
        dispatch({
          type: NO_ADDONS_COVERAGE_CHANGED,
          data: value ?? [],
        });
      },

  insuranceCoverageChanged:
    (value: string): AppThunkAction<ValidAction | {}> =>
      (dispatch, getState) => {
        dispatch({
          type: INSURANCE_SELECTED_COVERAGE_CHANGED,
          payload: { value: value },
        });

        const state = getState() as ApplicationState;
        const currentCoverageState = state?.insurance?.coverage;
        const currentCoverage = currentCoverageState?.Coverages?.find(i => i.InternalCode === currentCoverageState.selectedCode);

        const selectedAddonInternalCodes = state?.addon?.selectedAddOnInternalCodes ?? [];

        const list =
          currentCoverage?.CoverageAddons?.filter(i => !!i.InternalCode && selectedAddonInternalCodes?.indexOf(i.InternalCode) !== -1).map(i => i.InternalCode as string) ?? [];

        dispatch(actions.coverageNoChanged(currentCoverage?.CoverageAddons));
        dispatch(actions.coverageChanged(currentCoverage as Coverage, list));
      },

  customerEmailChanged:
    (value: unknown): AppThunkAction<ValidAction | {}> =>
      dispatch => {
        dispatch({
          type: CONTACTS_EMAIL_CHANGED as any,
          payload: { value: value as string },
        });
      },
  customerPhoneChanged:
    (value: unknown): AppThunkAction<ValidAction | {}> =>
      dispatch => {
        dispatch({
          type: CONTACTS_PHONE_CHANGED as any,
          payload: { value: value as string },
        });
      },
  actualMilageChanged:
    (value: unknown): AppThunkAction<ValidAction | {}> =>
      dispatch => {
        dispatch({
          type: ACTUAL_MILAGE_CHANGED as any,
          payload: { value: value as string },
        });
        dispatch(remoteNOActions.requestPriceData());
      },
  gdprChanged:
    (value: unknown): AppThunkAction<ValidAction | {}> =>
      dispatch => {
        dispatch({
          type: GDPR_CHANGED,
          payload: { value: value as boolean },
        });
      },
};
